<template>
  <div class="ganghaolai-mall">
    <section class="mall-hero" aria-labelledby="mall-title">
      <div class="mall-shell mall-hero__inner">
        <div class="mall-hero__copy">
          <h1 id="mall-title">刚好来商城</h1>
          <p class="mall-hero__statement">自营好物与本地商家，共建一站式生活消费平台</p>
          <p class="mall-hero__summary">
            刚好来商城以自营超市为品质底座，连接本地优质商家，提供线上选购、到店自提、同城配送和商家经营等服务，让消费者买得方便，也让本地商家拥有更直接的线上经营入口。
          </p>
          <div class="mall-actions">
            <router-link class="mall-button mall-button--primary" to="/investment">
              商家入驻 <i class="el-icon-right" aria-hidden="true"></i>
            </router-link>
            <router-link class="mall-button mall-button--secondary" to="/goin">
              咨询合作 <i class="el-icon-chat-line-square" aria-hidden="true"></i>
            </router-link>
          </div>
        </div>
        <figure class="mall-hero__visual">
          <div class="mall-phone">
            <img
              :src="mallHomeImage"
              alt="刚好来商城首页，展示自营超市、线下实体、自提配送、商品分类与商家入驻入口"
              width="1280"
              height="2772"
              decoding="async"
              fetchpriority="high"
            />
          </div>
          <figcaption>刚好来商城真实界面</figcaption>
        </figure>
      </div>
    </section>

    <section class="mall-model" aria-labelledby="mall-model-title">
      <div class="mall-shell">
        <header class="mall-section-head" v-reveal>
          <h2 id="mall-model-title">一个商城，两种经营力量</h2>
          <p>自营供给建立稳定基础，本地商家带来更多特色商品与服务。</p>
        </header>
        <div class="mall-model__grid" v-reveal="90">
          <article
            v-for="model in businessModels"
            :key="model.title"
            :class="`mall-model-card mall-model-card--${model.tone}`"
          >
            <span class="mall-icon"><i :class="model.icon" aria-hidden="true"></i></span>
            <h3>{{ model.title }}</h3>
            <p>{{ model.detail }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="mall-journey" aria-labelledby="mall-journey-title">
      <div class="mall-shell">
        <header class="mall-section-head mall-section-head--center" v-reveal>
          <h2 id="mall-journey-title">从发现好物到收到商品，更简单一步</h2>
          <p>围绕本地日常消费，让浏览、购买、履约和反馈形成清晰连续的体验。</p>
        </header>
        <ol class="mall-journey__list" v-reveal="90">
          <li v-for="(step, index) in shoppingSteps" :key="step.title">
            <span class="mall-journey__number">{{ String(index + 1).padStart(2, "0") }}</span>
            <span class="mall-icon"><i :class="step.icon" aria-hidden="true"></i></span>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.detail }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <section class="mall-merchant" aria-labelledby="mall-merchant-title">
      <div class="mall-shell">
        <header class="mall-section-head mall-section-head--split" v-reveal>
          <h2 id="mall-merchant-title">让本地好商家拥有自己的线上经营阵地</h2>
          <p>从合作意向到线上展示与履约协同，为商家提供清晰、可持续的经营入口。</p>
        </header>
        <div class="mall-merchant__grid" v-reveal="90">
          <article v-for="capability in merchantCapabilities" :key="capability.title">
            <span class="mall-icon"><i :class="capability.icon" aria-hidden="true"></i></span>
            <h3>{{ capability.title }}</h3>
            <p>{{ capability.detail }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="mall-value" aria-labelledby="mall-value-title">
      <div class="mall-shell">
        <header class="mall-section-head mall-section-head--center mall-section-head--light" v-reveal>
          <h2 id="mall-value-title">连接消费者、商家与本地生活</h2>
          <p>让每一方都在真实、可持续的本地消费场景中获得价值。</p>
        </header>
        <div class="mall-value__grid" v-reveal="90">
          <article v-for="value in platformValues" :key="value.title">
            <span>{{ value.label }}</span>
            <h3>{{ value.title }}</h3>
            <p>{{ value.detail }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="mall-feedback" aria-labelledby="mall-feedback-title">
      <div class="mall-shell mall-feedback__inner">
        <div class="mall-feedback__copy" v-reveal>
          <h2 id="mall-feedback-title">认真听取每一条意见，让商城持续变得更好</h2>
          <p>刚好来商城通过购物体验反馈和用户问卷了解真实需求，将意见用于优化商品、服务与使用体验。</p>
          <p class="mall-feedback__note">问卷海报用于展示真实运营动作，不替代本页面的商家入驻或咨询入口。</p>
        </div>
        <figure class="mall-feedback__poster" v-reveal="120">
          <img
            :src="feedbackImage"
            alt="刚好来商城用户意见问卷海报"
            width="2160"
            height="3840"
            decoding="async"
            loading="lazy"
          />
        </figure>
      </div>
    </section>

    <section class="mall-cta" aria-labelledby="mall-cta-title">
      <div class="mall-shell mall-cta__inner">
        <div>
          <h2 id="mall-cta-title">携手刚好来，共建本地商业新生态</h2>
          <p>无论是希望拓展线上经营的本地商家，还是寻求数字化合作的伙伴，都可以通过现有入口进一步了解合作方式。</p>
        </div>
        <div class="mall-actions">
          <router-link class="mall-button mall-button--primary" to="/investment">商家入驻</router-link>
          <router-link class="mall-button mall-button--secondary" to="/goin">咨询合作</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import mallHomeImage from "../assets/img/ganghaolai-mall-home.jpg";
import feedbackImage from "../assets/img/ganghaolai-mall-feedback.png";

export default {
  name: "GanghaolaiMall",
  data() {
    return {
      mallHomeImage,
      feedbackImage,
      businessModels: [
        {
          tone: "self",
          icon: "el-icon-shopping-bag-1",
          title: "自营超市",
          detail: "围绕饮品饮料、休闲零食、日用百货、厨房用品、家用电器、洗护用品等日常需求组织商品供给，通过平台自营保障基础品类的稳定展示与便捷购买。"
        },
        {
          tone: "merchant",
          icon: "el-icon-office-building",
          title: "商家入驻",
          detail: "面向本地零售、生活服务及特色商家开放入驻，通过线上店铺、商品展示和平台内容宣传，补充更丰富的本地商品与服务，形成开放协同的商业生态。"
        }
      ],
      shoppingSteps: [
        { icon: "el-icon-search", title: "分类浏览与搜索", detail: "按商品名称和品类快速查找所需商品。" },
        { icon: "el-icon-goods", title: "在线选购", detail: "查看商品信息并完成线上选购。" },
        { icon: "el-icon-truck", title: "灵活履约", detail: "根据商品和商家实际配置选择到店自提或配送。" },
        { icon: "el-icon-chat-line-square", title: "服务反馈", detail: "通过售后、评价与意见收集帮助平台持续改进。" }
      ],
      merchantCapabilities: [
        { icon: "el-icon-document-add", title: "快速入驻", detail: "通过招商合作入口提交合作意向，由平台进行后续沟通与审核。" },
        { icon: "el-icon-monitor", title: "线上展示", detail: "建立商城店铺，集中呈现品牌、商品和服务信息。" },
        { icon: "el-icon-position", title: "本地触达", detail: "借助商城入口与平台宣传连接本地消费者。" },
        { icon: "el-icon-connection", title: "经营协同", detail: "结合实际业务选择自提或配送，并获得平台运营与服务支持。" }
      ],
      platformValues: [
        { label: "消费者", title: "更方便的本地购物", detail: "获得品类更丰富、购买更便捷、服务更贴近本地生活的购物体验。" },
        { label: "商家", title: "更清晰的线上入口", detail: "获得线上展示、商品经营、用户触达和合作服务入口。" },
        { label: "平台", title: "更完善的商业供给", detail: "通过自营商品与开放商家协同，持续完善本地商业供给和服务能力。" }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
.ganghaolai-mall,
.ganghaolai-mall * {
  box-sizing: border-box;
}

.ganghaolai-mall {
  min-width: 0;
  overflow: hidden;
  color: var(--ink-800);
  background: #fff;
}

.ganghaolai-mall h1,
.ganghaolai-mall h2,
.ganghaolai-mall h3,
.ganghaolai-mall p {
  margin: 0;
  letter-spacing: 0;
}

.ganghaolai-mall h1,
.ganghaolai-mall h2,
.ganghaolai-mall h3,
.ganghaolai-mall p,
.ganghaolai-mall a,
.ganghaolai-mall span {
  overflow-wrap: anywhere;
}

.mall-shell {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding-right: 24px;
  padding-left: 24px;
}

.mall-hero {
  position: relative;
  min-height: 560px;
  overflow: hidden;
  border-bottom: 1px solid var(--line);
  background: #f4f9fc;
}

.mall-hero__inner {
  position: relative;
  min-height: 560px;
  padding-top: 76px;
  padding-bottom: 68px;
}

.mall-hero__copy {
  position: relative;
  z-index: 2;
  width: min(56%, 650px);
}

.mall-hero h1 {
  color: var(--ink-950);
  font-size: var(--text-display);
  font-weight: 700;
  line-height: 1.08;
}

.mall-hero__statement {
  margin-top: 18px !important;
  color: var(--brand-700);
  font-size: clamp(20px, 2.2vw, 26px);
  font-weight: 600;
  line-height: 1.45;
}

.mall-hero__summary {
  max-width: 610px;
  margin-top: 22px !important;
  color: var(--ink-600);
  font-size: 16px;
  line-height: 1.9;
}

.mall-actions {
  display: flex;
  margin-top: 30px;
  flex-wrap: wrap;
  gap: 12px;
}

.mall-button {
  display: inline-flex;
  min-height: 46px;
  padding: 0 22px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid var(--brand-700);
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none;
  transition: background-color 200ms ease, color 200ms ease, border-color 200ms ease, transform 200ms ease;
}

.mall-button:hover {
  transform: translateY(-1px);
}

.mall-button--primary {
  color: #fff;
  background: var(--brand-700);
}

.mall-button--primary:hover {
  border-color: var(--brand-800);
  background: var(--brand-800);
}

.mall-button--secondary {
  color: var(--brand-700);
  background: #fff;
}

.mall-button--secondary:hover {
  color: var(--brand-800);
  border-color: var(--brand-800);
}

.mall-button:focus-visible {
  outline: 3px solid rgba(18, 97, 142, 0.38);
  outline-offset: 4px;
}

.mall-hero__visual {
  position: absolute;
  top: 28px;
  right: 72px;
  z-index: 1;
  width: min(30vw, 320px);
  margin: 0;
}

.mall-phone {
  width: 100%;
  height: 500px;
  overflow: hidden;
  border: 8px solid var(--brand-950);
  border-radius: var(--radius-md);
  background: #fff;
  box-shadow: var(--shadow-lg);
}

.mall-phone img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}

.mall-hero__visual figcaption {
  margin-top: 10px;
  color: var(--ink-600);
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
}

.mall-model,
.mall-journey,
.mall-merchant,
.mall-value,
.mall-feedback {
  padding: var(--section-space) 0;
}

.mall-section-head {
  max-width: 760px;
  margin-bottom: 46px;
}

.mall-section-head h2,
.mall-feedback__copy h2 {
  color: var(--ink-950);
  font-size: var(--text-h2);
  font-weight: 700;
  line-height: 1.32;
}

.mall-section-head p,
.mall-feedback__copy p {
  max-width: 680px;
  margin-top: 15px;
  color: var(--ink-600);
  font-size: 16px;
  line-height: 1.85;
}

.mall-section-head--center {
  margin-right: auto;
  margin-left: auto;
  text-align: center;
}

.mall-section-head--center p {
  margin-right: auto;
  margin-left: auto;
}

.mall-section-head--split {
  display: grid;
  max-width: none;
  align-items: end;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.7fr);
  gap: 48px;
}

.mall-section-head--split p {
  margin-top: 0;
}

.mall-model__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.mall-model-card {
  min-width: 0;
  padding: 34px;
  border: 1px solid var(--line);
  border-top-width: 3px;
  border-radius: var(--radius-md);
  background: #fff;
  box-shadow: var(--shadow-xs);
}

.mall-model-card--self {
  border-top-color: var(--tone-fresh);
  background: var(--tone-fresh-soft);
}

.mall-model-card--merchant {
  border-top-color: var(--tone-merchant);
  background: var(--tone-merchant-soft);
}

.mall-icon {
  display: inline-grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border-radius: 8px;
  color: var(--brand-700);
  background: var(--brand-50);
  font-size: 21px;
}

.mall-model-card--self .mall-icon {
  color: var(--tone-fresh);
  background: #e6f5ed;
}

.mall-model-card--merchant .mall-icon,
.mall-merchant__grid .mall-icon {
  color: var(--tone-merchant);
  background: #fff0df;
}

.mall-model-card h3,
.mall-merchant__grid h3,
.mall-journey__list h3,
.mall-value__grid h3 {
  margin-top: 18px;
  color: var(--ink-950);
  font-size: 21px;
  line-height: 1.4;
}

.mall-model-card--self h3 {
  color: var(--tone-fresh);
}

.mall-model-card--merchant h3,
.mall-merchant__grid h3 {
  color: var(--tone-merchant-deep);
}

.mall-model-card p,
.mall-merchant__grid p,
.mall-journey__list p,
.mall-value__grid p {
  margin-top: 10px;
  color: var(--ink-600);
  font-size: 15px;
  line-height: 1.8;
}

.mall-journey {
  background: var(--surface-soft);
}

.mall-journey__list {
  position: relative;
  display: grid;
  margin: 0;
  padding: 0;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  list-style: none;
}

.mall-journey__list::before {
  position: absolute;
  top: 23px;
  right: 12.5%;
  left: 12.5%;
  height: 2px;
  content: "";
  background: var(--brand-100);
}

.mall-journey__list li {
  position: relative;
  min-width: 0;
  padding: 0 24px 0 0;
}

.mall-journey__list li + li {
  padding-left: 24px;
  border-left: 1px solid var(--line);
}

.mall-journey__number {
  position: relative;
  z-index: 1;
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  border: 2px solid var(--brand-700);
  border-radius: 50%;
  color: #fff;
  background: var(--brand-700);
  font-size: 14px;
  font-weight: 700;
}

.mall-journey__list .mall-icon {
  margin-top: 22px;
}

.mall-merchant__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
}

.mall-merchant__grid article {
  min-width: 0;
  padding: 0 24px;
  border-top: 2px solid rgba(215, 120, 36, 0.48);
}

.mall-merchant__grid article:first-child {
  padding-left: 0;
}

.mall-merchant__grid article + article {
  border-left: 1px solid var(--line);
}

.mall-merchant__grid article:last-child {
  padding-right: 0;
}

.mall-merchant__grid .mall-icon {
  margin-top: 22px;
}

.mall-value {
  color: #fff;
  background: var(--brand-950);
}

.mall-section-head--light h2 {
  color: #fff;
}

.mall-section-head--light p {
  color: rgba(255, 255, 255, 0.72);
}

.mall-value__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.mall-value__grid article {
  min-width: 0;
  padding: 10px 34px;
  border-right: 1px solid var(--glass-border);
}

.mall-value__grid article:first-child {
  padding-left: 0;
}

.mall-value__grid article:last-child {
  padding-right: 0;
  border-right: 0;
}

.mall-value__grid span {
  color: var(--accent-300);
  font-size: 12px;
  font-weight: 700;
}

.mall-value__grid h3 {
  color: #fff;
}

.mall-value__grid p {
  color: rgba(255, 255, 255, 0.72);
}

.mall-feedback {
  background: var(--surface-tint);
}

.mall-feedback__inner {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 0.88fr) minmax(280px, 0.62fr);
  gap: 72px;
}

.mall-feedback__copy h2 {
  max-width: 600px;
}

.mall-feedback__copy p {
  max-width: 600px;
}

.mall-feedback__note {
  padding-left: 14px;
  border-left: 3px solid var(--brand-500);
  font-size: 14px !important;
}

.mall-feedback__poster {
  width: min(100%, 360px);
  aspect-ratio: 9 / 16;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid rgba(22, 121, 173, 0.2);
  border-radius: var(--radius-md);
  background: #fff;
  box-shadow: var(--shadow-md);
}

.mall-feedback__poster img {
  display: block;
  width: 100%;
  height: auto;
}

.mall-cta {
  padding: 70px 0;
  border-top: 1px solid var(--line);
  background: #fff;
}

.mall-cta__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 42px;
}

.mall-cta h2 {
  color: var(--ink-950);
  font-size: var(--text-h2);
  line-height: 1.3;
}

.mall-cta p {
  margin-top: 12px;
  color: var(--ink-600);
  font-size: 16px;
  line-height: 1.75;
}

.mall-cta .mall-actions {
  margin-top: 0;
  flex: 0 0 auto;
}

@media (max-width: 1080px) {
  .mall-hero__visual {
    right: 32px;
  }

  .mall-journey__list,
  .mall-merchant__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 34px;
  }

  .mall-journey__list::before {
    display: none;
  }

  .mall-journey__list li:nth-child(odd),
  .mall-merchant__grid article:nth-child(odd) {
    padding-left: 0;
    border-left: 0;
  }

  .mall-journey__list li:nth-child(even),
  .mall-merchant__grid article:nth-child(even) {
    padding-right: 0;
  }
}

@media (max-width: 768px) {
  .mall-shell {
    padding-right: 16px;
    padding-left: 16px;
  }

  .mall-hero {
    min-height: 0;
  }

  .mall-hero__inner {
    min-height: 0;
    padding-top: 48px;
    padding-bottom: 50px;
  }

  .mall-hero__copy {
    width: 100%;
  }

  .mall-hero h1 {
    font-size: 38px;
  }

  .mall-hero__statement {
    font-size: 21px;
  }

  .mall-hero__summary {
    font-size: 15px;
    line-height: 1.8;
  }

  .mall-hero__visual {
    position: relative;
    top: auto;
    right: auto;
    width: 250px;
    margin: 42px auto 0;
  }

  .mall-phone {
    height: 330px;
    border-width: 6px;
    border-radius: 16px;
  }

  .mall-model,
  .mall-journey,
  .mall-merchant,
  .mall-value,
  .mall-feedback {
    padding: 62px 0;
  }

  .mall-section-head,
  .mall-section-head--split {
    display: block;
    margin-bottom: 32px;
  }

  .mall-section-head h2,
  .mall-feedback__copy h2 {
    font-size: 28px;
  }

  .mall-section-head p,
  .mall-feedback__copy p {
    font-size: 15px;
  }

  .mall-section-head--split p {
    margin-top: 14px;
  }

  .mall-model__grid,
  .mall-feedback__inner,
  .mall-value__grid {
    grid-template-columns: 1fr;
  }

  .mall-model-card {
    padding: 28px 24px;
  }

  .mall-journey__list,
  .mall-merchant__grid {
    grid-template-columns: 1fr;
    row-gap: 28px;
  }

  .mall-journey__list li,
  .mall-journey__list li + li,
  .mall-merchant__grid article,
  .mall-merchant__grid article:first-child,
  .mall-merchant__grid article:last-child {
    padding-right: 0;
    padding-left: 0;
    border-left: 0;
  }

  .mall-journey__list li + li,
  .mall-merchant__grid article + article {
    padding-top: 28px;
    border-top: 1px solid var(--line);
  }

  .mall-journey__list .mall-icon,
  .mall-merchant__grid .mall-icon {
    margin-top: 16px;
  }

  .mall-value__grid article,
  .mall-value__grid article:first-child,
  .mall-value__grid article:last-child {
    padding: 24px 0;
    border-right: 0;
    border-bottom: 1px solid var(--glass-border);
  }

  .mall-value__grid article:last-child {
    border-bottom: 0;
  }

  .mall-feedback__inner {
    gap: 34px;
  }

  .mall-feedback__poster {
    max-width: 320px;
  }

  .mall-cta__inner {
    align-items: flex-start;
    flex-direction: column;
  }

  .mall-cta .mall-actions {
    margin-top: 0;
  }
}

@media (max-width: 420px) {
  .mall-actions {
    width: 100%;
  }

  .mall-button {
    min-width: 0;
    flex: 1 1 140px;
    padding-right: 14px;
    padding-left: 14px;
  }

  .mall-hero__visual {
    width: 232px;
  }

  .mall-phone {
    height: 310px;
  }

  .mall-model-card {
    padding: 26px 22px;
  }

  .mall-feedback__poster {
    max-width: 300px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ganghaolai-mall,
  .ganghaolai-mall *,
  .ganghaolai-mall *::before,
  .ganghaolai-mall *::after {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
</style>
